import { clamp01 } from './parapheBoxes';

export type RefuseKind = 'highlight' | 'note';

export interface RefuseAnnotation {
  id: string;
  kind: RefuseKind;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  text?: string;
}

const NOTE_W = 0.18;
const NOTE_H = 0.08;
const MIN_HIGHLIGHT = 0.008;

export function rectFromDrag(x0: number, y0: number, x1: number, y1: number): {
  x: number; y: number; w: number; h: number;
} {
  const xa = clamp01(x0);
  const ya = clamp01(y0);
  const xb = clamp01(x1);
  const yb = clamp01(y1);
  const x = Math.min(xa, xb);
  const y = Math.min(ya, yb);
  return { x, y, w: Math.max(xb, xa) - x, h: Math.max(yb, ya) - y };
}

function isAnnotation(v: unknown): v is RefuseAnnotation {
  if (!v || typeof v !== 'object') return false;
  const a = v as Record<string, unknown>;
  if (a.kind !== 'highlight' && a.kind !== 'note') return false;
  if (typeof a.id !== 'string' || a.id.length === 0) return false;
  if (typeof a.page !== 'number' || a.page < 1) return false;
  if (typeof a.x !== 'number' || typeof a.y !== 'number') return false;
  if (typeof a.w !== 'number' || typeof a.h !== 'number') return false;
  if (a.text != null && typeof a.text !== 'string') return false;
  return true;
}

function trimmedText(text: string | undefined): string | undefined {
  const t = text?.trim();
  return t ? t : undefined;
}

export function hasRefuseMarks(annotations: readonly unknown[]): boolean {
  return annotations.length >= 1;
}

export function parseRefuseAnnotations(raw: unknown): RefuseAnnotation[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isAnnotation).map((a) => ({
    ...a,
    text: trimmedText(a.text),
  }));
}

export function addHighlight(
  list: RefuseAnnotation[],
  page: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  id: string,
  text?: string,
): RefuseAnnotation[] {
  const r = rectFromDrag(x0, y0, x1, y1);
  if (r.w < MIN_HIGHLIGHT || r.h < MIN_HIGHLIGHT) return list;
  return [...list, { id, kind: 'highlight', page, ...r, text: trimmedText(text) }];
}

export function addNote(
  list: RefuseAnnotation[],
  page: number,
  clickX: number,
  clickY: number,
  text: string,
  id: string,
): RefuseAnnotation[] {
  const w = NOTE_W;
  const h = NOTE_H;
  const x = Math.min(clamp01(clickX), 1 - w);
  const y = Math.min(clamp01(clickY), 1 - h);
  return [...list, { id, kind: 'note', page, x, y, w, h, text: trimmedText(text) }];
}

export function moveAnnotation(a: RefuseAnnotation, x: number, y: number): RefuseAnnotation {
  return {
    ...a,
    x: Math.min(clamp01(x), 1 - a.w),
    y: Math.min(clamp01(y), 1 - a.h),
  };
}

export function removeAnnotation(list: RefuseAnnotation[], id: string): RefuseAnnotation[] {
  return list.filter((a) => a.id !== id);
}

export function setAnnotationText(
  list: RefuseAnnotation[],
  id: string,
  text: string,
): RefuseAnnotation[] {
  return list.map((a) => (a.id === id ? { ...a, text: text.length === 0 ? undefined : text } : a));
}

export function noteTexts(list: RefuseAnnotation[]): string[] {
  return list
    .map((a) => trimmedText(a.text))
    .filter((t): t is string => Boolean(t));
}

export function annotationEntries(list: RefuseAnnotation[]): Array<{
  id: string;
  page: number;
  kind: RefuseKind;
  text: string;
}> {
  return [...list]
    .sort((a, b) => a.page - b.page)
    .map((a) => ({
      id: a.id,
      page: a.page,
      kind: a.kind,
      text: trimmedText(a.text) ?? '',
    }));
}
